import {
  AvatarGroup,
  Box,
  Collapse,
  Fade,
  Grow,
  Stack,
  Typography,
} from "@mui/material";
import { Avatar, EyeCloseIcon, PinFilledIcon } from "components";
import React, { useEffect, useMemo } from "react";
import AvatarChat from "./components/AvatarChat";
import { usePinnedStore } from "controller/store/pinned-chat/pinnedChatStore";
import {
  useChatNotificationStore,
  useGetAllPinnedService,
  useGlobalStore,
  usePermissionStore,
} from "controller";
import { PERMISSIONS } from "shared";

const PinnedChat = () => {
  const hasTicketPermission = usePermissionStore((state) =>
    state.hasPermissions(
      [PERMISSIONS.SD.SD_TICKET_ALL_RW, PERMISSIONS.SD.SD_TICKET_ASSIGNED_RW],
      "OR",
    ),
  );
  const hasProjectPermission = usePermissionStore((state) =>
    state.hasPermissions(
      [PERMISSIONS.SD.SD_PROJECT_ALL_RW, PERMISSIONS.SD.SD_PROJECT_ASSIGNED_RW],
      "OR",
    ),
  );
  const hasTaskPermission = usePermissionStore((state) =>
    state.hasPermissions(
      [PERMISSIONS.SD.SD_TASK_ALL_RW, PERMISSIONS.SD.SD_TASK_ASSIGNED_RW],
      "OR",
    ),
  );
  const permittedServices = [
    ...(hasTicketPermission ? ["ticket"] : []),
    ...(hasProjectPermission ? ["project"] : []),
    ...(hasTaskPermission ? ["task"] : []),
  ];
  const pinnedChat = usePinnedStore((state) => state.pinnedChat);
  const pinnedChatArr = Object.values(pinnedChat);
  const filteredPinnedChats =
    pinnedChatArr?.filter((chat) =>
      permittedServices.includes(chat?.serviceType),
    ) || [];
  const setPinnedChat = usePinnedStore((state) => state.setPinnedChats);
  const { data: currentPinnedChats } = useGetAllPinnedService();
  const showPinnedChats = useGlobalStore((state) => state.showPinnedChats);
  const toggleShowPinnedChats = useGlobalStore(
    (state) => state.toggleShowPinnedChats,
  );

  const allChatMessages = useChatNotificationStore(
    (state) => state.allChatNotifications,
  );
  const totalPinnedChatMessageCount = useMemo(() => {
    return allChatMessages
      .filter(
        (item) =>
          Boolean(pinnedChat[item.service_id]) &&
          permittedServices.includes(pinnedChat[item.service_id]?.serviceType), // check if has access to the particular service ("ticket" or "project" or "task")
      )
      .reduce((acc, item) => acc + (item?.unseenCount || 0), 0);
  }, [pinnedChat, allChatMessages]);
  useEffect(() => {
    setPinnedChat(
      currentPinnedChats && Array.isArray(currentPinnedChats)
        ? currentPinnedChats
        : [],
    );
  }, [currentPinnedChats]);

  return filteredPinnedChats.length > 0 ? (
    <Stack
      sx={{
        width: "fit-content",
        height: "fit-content",
        position: "absolute",
        bottom: "0px",
        left: "100px",
        zIndex: 501,
        maxWidth: "calc(100vw - 100px)",
        transition: "all 0.3s ease-in",
        gap: showPinnedChats ? "8px" : "0px",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          maxWidth: "calc(100vw - 100px)",
          overflowX: "scroll",
        }}
      >
        <AvatarGroup
          max={7}
          sx={{
            flexDirection: "row",
            width: "fit-content",
            // overflowX: "scroll",
            pl: "16px",
            pr: "32px",

            alignItems: "end",
            position: "fixed",
            left: "100px",
            bottom: "38px",
            display: "flex",

            height: "0",
          }}
        >
          {filteredPinnedChats.map((chat) => {
            return (
              <AvatarChat
                key={chat?.serviceId}
                serviceId={chat?.serviceId}
                serviceType={chat?.serviceType}
                summary={chat?.summary}
                assignees={chat?.assignees}
                serviceUid={chat?.serviceUid}
                customerName={chat?.customerName}
                customerUserName={chat?.customerUserName}
                primaryColor={chat?.colorCodePrimary}
                tintColor={chat?.colorCodeTint}
                profileImage={chat?.profileImage}
                serviceStatus={chat?.serviceStatus}
                isPinnedOpen={showPinnedChats}
              />
            );
          })}
        </AvatarGroup>
      </Box>
      <Box
        sx={{
          height: "32px",
          width: "97px",
          display: "flex",
          background:
            "linear-gradient(278.39deg, #0C1324 20.44%, #00163F 86.57%)",
          borderWidth: "1.5px 1.5px 0px 1.5px",
          borderStyle: "solid",
          borderRadius: "16px 16px 0px 0px",

          cursor: "pointer",
        }}
      >
        {showPinnedChats ? (
          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              px: "12px",
            }}
            onClick={toggleShowPinnedChats}
          >
            <EyeCloseIcon
              sx={{
                width: "16px",
                height: "16px",
                color: "white",
              }}
            />
            <Typography variant="caption-2" color={"white"}>
              Hide
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              px: "12px",
            }}
            onClick={toggleShowPinnedChats}
          >
            <PinFilledIcon
              sx={{
                width: "16px",
                height: "16px",
                color: "white",
              }}
            />
            <Typography variant="caption-2" color={"white"}>
              Chats
            </Typography>
            {totalPinnedChatMessageCount > 0 && (
              <Box
                sx={{
                  backgroundColor: "red",
                  borderRadius: "16px",
                  width: "fit-content",
                  height: "16px",
                  minWidth: "16px",
                  px: "2px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ color: "white" }} variant="caption-xs">
                  {totalPinnedChatMessageCount < 100
                    ? totalPinnedChatMessageCount
                    : "99+"}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  ) : null;
};

export default PinnedChat;
// bgcolor: "linear-gradient(278.39deg, #0C1324 20.44%, #00163F 86.57%)",
// border: "1.5px solid",
// borderImageSource:
//   "linear-gradient(109.75deg, #1449F7 11.83%, #0C1224 39.99%)",
